import React, { useEffect, useState, useRef } from "react";

const EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
];
const DynamicForm = ({ fields, formData, handleChange, errors = {}, }) => {
  const wrapperRef = useRef(null);
  const getLocalizedOptionLabel = (option) =>
    option.label?.en_US || option.reference || option.value || "";

  const renderField = (field) => {
    const value = formData[field.fieldReference] || "";

    switch (field.type) {
      case "text":
        return field.displayStyle === "multiline" ? (
          <textarea
            value={value}
            className="form-input "
            placeholder={field.placeholder?.en_US || ""}
            onChange={(e) =>
              handleChange(field.fieldReference, e.target.value)
            }
            maxLength={
              field.fieldReference === "Description" ? 4000 : undefined
            }
          />
        ) : (
          <div className="proposal-autocomplete-wrapper" ref={wrapperRef}>
            <input
              className="form-input "
              type="text"
              value={value}
              placeholder={field.placeholder?.en_US || ""}
              onChange={
                field.fieldReference === "emailID"
                  ? (e) => handleEmailChange(e, field)
                  : (e) => handleChange(field.fieldReference, e.target.value)
              }
              onKeyDown={field.fieldReference === "emailID" ? handleKeyDown : undefined}
              maxLength={
                field.fieldReference === "city" ? 50 :
                  field.fieldReference === "companyName" ? 150 :
                    field.fieldReference === "website" ? 255 :
                      field.fieldReference === "contactPerson" ? 80 :
                        field.fieldReference === "designation" ? 80 :
                          field.fieldReference === "emailID" ? 254 :
                            field.fieldReference === "proposalTopic" ? 150 : 100
              }
            />
            {field.fieldReference === "emailID" && showSuggestions && suggestions.length > 0 && (
              <div className="proposal-autocomplete-dropdown">
                {suggestions.map((domain, index) => (
                  <div
                    key={domain}
                    ref={(el) => (itemRefs.current[index] = el)}
                    className={`proposal-autocomplete-option ${activeIndex === index ? "active" : ""
                      }`}
                    onMouseDown={() => handleSelect(domain)}
                  >
                    {(value || "").split("@")[0]}@{domain}
                  </div>
                ))}
              </div>
            )}
          </div>

        );

      case "numeric":
        return (
          <input
            type="text"
            className="form-input"
            inputMode="numeric"
            value={value}
            placeholder={field.placeholder?.en_US || ""}
            maxLength={field.maxLength || 10}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");

              handleChange(
                field.fieldReference,
                val.slice(0, field.maxLength || 10)
              );
            }}
          />
        );

      case "date":
        return (
          <input
            type="date"
            className="form-input"
            value={value}
            onChange={(e) =>
              handleChange(field.fieldReference, e.target.value)
            }
          />
        );

      case "select": {
        return (
          <select
            value={value}
            className="form-input"
            onChange={(e) =>
              handleChange(field.fieldReference, e.target.value)
            }
          >
            {field.options?.map((option) => (
              <option
                key={option.value}
                value={getLocalizedOptionLabel(option)}
              >
                {getLocalizedOptionLabel(option)}
              </option>
            ))}
          </select>
        )
      }
      case "radio":
        return (
          <div>
            {field.options?.map((option) => (
              <label key={option.value}>
                <input
                  type="radio"
                  name={field.fieldReference}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) =>
                    handleChange(
                      field.fieldReference,
                      e.target.value
                    )
                  }
                />
                {option.label?.en_US}
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) =>
              handleChange(
                field.fieldReference,
                e.target.checked
              )
            }
          />
        );

      default:
        return null;
    }
  };


  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemRefs = useRef([]);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setActiveIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    }
    else if (e.key === "ArrowUp") {
      e.preventDefault();

      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    }

    else if (e.key === "PageDown") {
      e.preventDefault();

      setActiveIndex((prev) =>
        Math.min(prev + 1, suggestions.length - 1)
      );
    }

    else if (e.key === "PageUp") {
      e.preventDefault();

      setActiveIndex((prev) =>
        Math.max(prev - 1, 0)
      );
    }

    else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    }
  };

  useEffect(() => {
    if (activeIndex >= 0) {
      itemRefs.current[activeIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeIndex]);
  const handleEmailChange = (e, field) => {
    const val = e.target.value;

    handleChange(field.fieldReference, val);

    const atIndex = val.indexOf("@");

    if (atIndex !== -1) {
      const typedDomain = val.slice(atIndex + 1).toLowerCase();

      const filtered = EMAIL_DOMAINS.filter(domain =>
        domain.startsWith(typedDomain)
      );

      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setActiveIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (domain) => {
    const currentEmail = formData["emailID"] || "";

    const namePart = currentEmail.split("@")[0];
    const newEmail = `${namePart}@${domain}`;

    if (newEmail.length > 50) {
      return;
    }
    handleChange("emailID", newEmail);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {fields?.map((field) => (
        <div
          className="form-row"
          key={field.fieldReference}
        >
          <label className="label-one ">
            {field.label?.en_US}
            {field.required && (
              <span style={{
                color: "#97262A"
              }}> *</span>
            )}
          </label>

          {renderField(field)}

          {errors?.[field.fieldReference] && (
            <span className="error-text">
              {errors[field.fieldReference]}
            </span>
          )}
        </div>
      ))}
    </>
  );
};

export default DynamicForm;